import { useCallback, useEffect, useState } from 'react';
import useSWR from 'swr';

import { meWhatsNewPartialUpdate, meWhatsNewRetrieve } from '~/data/generated/endpoints/me/me';
import { type WhatsNewState } from '~/data/generated/model';
import { type ChangelogEntry, changelogSchema } from '~/data/schemas/changelog';
import { liftAiAuth } from '~/utils/api';

// Same-origin by default: the app serves /changelog.json (built from
// docs/changelog.mdx by scripts/generate-changelog.mjs). Override with an absolute
// URL via VITE_CHANGELOG_URL if the changelog is hosted elsewhere.
const CHANGELOG_URL = import.meta.env.VITE_CHANGELOG_URL ?? '/changelog.json';

export const WHATS_NEW_SEEN_KEY = 'whats-new-seen';

// Someone with no seen-state yet only hears about updates from the last 30 days.
const NEW_USER_WINDOW_DAYS = 30;

// Refresh roughly hourly. The changelog is a static file, so a stale entry for
// up to an hour is harmless.
const REFRESH_INTERVAL_MS = 60 * 60 * 1000;

// How often to check whether the local date has rolled over.
const DATE_CHECK_INTERVAL_MS = 60 * 1000;

async function fetchChangelog(url: string): Promise<ChangelogEntry[]> {
  const res = await fetch(url);
  if (!res.ok) throw new Error(`Changelog fetch failed: ${res.status}`);
  const json = await res.json();
  return changelogSchema.parse(json).entries;
}

async function fetchSeen(): Promise<WhatsNewState> {
  return (await meWhatsNewRetrieve()).data;
}

function newUserCutoff() {
  const cutoff = new Date();
  cutoff.setDate(cutoff.getDate() - NEW_USER_WINDOW_DAYS);
  return cutoff.toISOString().slice(0, 10);
}

// The user's local date as YYYY-MM-DD. Not toISOString(), which is the UTC date and
// runs a day ahead in US evenings.
function localToday() {
  const now = new Date();
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const day = String(now.getDate()).padStart(2, '0');
  return `${now.getFullYear()}-${month}-${day}`;
}

// Held in state so a tab left open overnight picks up the new date. A plain
// localToday() call in render is treated as constant by the React Compiler and
// never re-read while the component stays mounted.
function useLocalToday() {
  const [today, setToday] = useState(localToday);
  useEffect(() => {
    const id = setInterval(() => setToday(localToday()), DATE_CHECK_INTERVAL_MS);
    return () => clearInterval(id);
  }, []);
  return today;
}

const isImpersonating = () => Boolean(liftAiAuth().getImpersonation());

const later = (a: string | null | undefined, b: string) => (a && a > b ? a : b);

const NOTHING_SEEN: WhatsNewState = { seenOn: null, dialogSeenOn: null };

// Seen dates only move forward.
const advance = (current: WhatsNewState, patch: Partial<WhatsNewState>): WhatsNewState => ({
  seenOn: patch.seenOn ? later(current.seenOn, patch.seenOn) : current.seenOn,
  dialogSeenOn: patch.dialogSeenOn
    ? later(current.dialogSeenOn, patch.dialogSeenOn)
    : current.dialogSeenOn,
});

export function useWhatsNew() {
  const { data: changelog = [] } = useSWR(
    ['changelog', CHANGELOG_URL],
    () => fetchChangelog(CHANGELOG_URL),
    { refreshInterval: REFRESH_INTERVAL_MS, revalidateOnFocus: false, shouldRetryOnError: false },
  );
  // An entry dated after today isn't released yet. Saving its date would also fail: the
  // API ignores seen dates more than a day ahead, so the dialog would reopen on every load.
  const today = useLocalToday();
  const entries = changelog.filter((entry) => entry.date <= today);
  const { data: seen, mutate } = useSWR(WHATS_NEW_SEEN_KEY, fetchSeen, {
    shouldRetryOnError: false,
  });
  const save = useCallback(
    (patch: Partial<WhatsNewState>) => {
      // Built from what's on screen, so saves from the bell, card and dialog don't undo each other.
      let target: WhatsNewState = NOTHING_SEEN;
      void mutate(
        async (current) => {
          if (!current) return current;
          const next = advance(current, target);
          if (next.seenOn === current.seenOn && next.dialogSeenOn === current.dialogSeenOn) {
            return current;
          }
          // An admin viewing as a customer must not mark the customer's updates seen.
          if (isImpersonating()) return next;
          return advance((await meWhatsNewPartialUpdate(next)).data, next);
        },
        {
          optimisticData: (current, displayed) => {
            target = advance(displayed ?? current ?? NOTHING_SEEN, patch);
            return target;
          },
          rollbackOnError: true,
          revalidate: false,
          throwOnError: false,
        },
      );
    },
    [mutate],
  );

  const cutoff = seen?.seenOn ?? newUserCutoff();
  const dialogCutoff = seen?.dialogSeenOn ?? cutoff;

  const dialogEntries = seen
    ? entries.filter((entry) => entry.announce === 'dialog' && entry.date > dialogCutoff)
    : [];
  // Updates already watched in the dialog don't come back in the card.
  const unreadEntries = seen
    ? entries.filter(
        (entry) =>
          entry.date > cutoff &&
          !(entry.announce === 'dialog' && seen.dialogSeenOn && entry.date <= seen.dialogSeenOn),
      )
    : [];

  const newestDate = entries[0]?.date;
  const loaded = seen !== undefined;
  const markSeen = useCallback(() => {
    if (newestDate && loaded) save({ seenOn: newestDate });
  }, [newestDate, loaded, save]);

  const newestDialogDate = dialogEntries[0]?.date;
  const markDialogSeen = useCallback(() => {
    if (newestDialogDate) save({ dialogSeenOn: newestDialogDate });
  }, [newestDialogDate, save]);

  return { entries, unreadEntries, dialogEntries, markSeen, markDialogSeen };
}
